import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { request } from '@/data/request';
import { useAuthStore } from '@/stores/auth';
import { normalizeClientRoutePath, useAuthorizationStore } from '@/stores/authorization';
import type { FunctionalityPermission } from '@/stores/authorization';
import type { CustomReportResponse } from '@/views/reports/customReports/types';

export const CUSTOM_REPORT_NAVIGATION_QUERY_KEY = 'customReportNavigation';

/** Report shortcuts use the same context and execution permissions as the list. */
export function useCustomReportNavigation(): FunctionalityPermission[] {
  const user = useAuthStore((state) => state.user);
  const contextSelectionRequired = useAuthStore((state) => state.contextSelectionRequired);
  const isSelectingContext = useAuthStore((state) => state.isSelectingContext);
  const payload = useAuthorizationStore((state) => state.payload);
  const selectedModuleKey = useAuthorizationStore((state) => state.selectedModuleKey);
  const moduleKey = selectedModuleKey ?? (payload?.modules.length === 1 ? payload.modules[0].key : null);
  const parent = payload?.functionalities.find((item) => item.key === 'est_custom_reports');
  const routes = payload?.routes.map(normalizeClientRoutePath) ?? [];
  const enabled = Boolean(
    moduleKey === 'establishment' &&
    payload?.modules.some((module) => module.key === 'establishment') &&
    user?.establishment_config_id &&
    !contextSelectionRequired &&
    !isSelectingContext &&
    parent?.module_key === 'establishment' &&
    routes.includes('/reports/custom') &&
    routes.includes('/reports/custom/:id/run') &&
    payload?.components.some((item) => item.key === 'custom_reports_action_run'),
  );
  const { data } = useQuery({
    queryKey: [CUSTOM_REPORT_NAVIGATION_QUERY_KEY, user?.id, user?.establishment_config_id],
    queryFn: () => request<CustomReportResponse[]>({
      method: 'get',
      url: '/reports/custom',
      data: { is_active: true, limit: 0 },
    }),
    enabled,
  });

  return useMemo(() => {
    // Disabled queries may retain cached data; never expose it after a permission/context change.
    if (!enabled || !parent) return [];
    return (data ?? []).filter((report) => report.is_active).map((report, index) => ({
      key: `custom_report_${report.id}`,
      name: report.name,
      module_key: 'establishment',
      parent_key: parent.parent_key,
      route: `/reports/custom/${report.id}/run`,
      show_in_menu: parent.show_in_menu,
      menu_order: parent.menu_order + (index + 1) / ((data?.length ?? 0) + 1),
    }));
  }, [data, enabled, parent]);
}
